// Domain vocabulary shared by the server and every client.

export const INDENT_STATUSES = [
  'DRAFT',
  'REQUESTED',
  'APPROVED',
  'REJECTED',
  'CLAIMED',
  'FULFILLED',
  'CANCELLED',
  'REVOKED',
  'EXPIRED',
] as const;
export type IndentStatus = (typeof INDENT_STATUSES)[number];

/** Allowed server-side transitions. Anything else is refused. */
export const INDENT_TRANSITIONS: Record<IndentStatus, IndentStatus[]> = {
  DRAFT: ['REQUESTED', 'APPROVED', 'CANCELLED'],
  REQUESTED: ['APPROVED', 'REJECTED', 'CANCELLED', 'EXPIRED'],
  APPROVED: ['CLAIMED', 'REVOKED', 'CANCELLED', 'EXPIRED'],
  // A claim is the supply commitment point. It ends in a recorded supply, or
  // in an authorised "no goods were supplied" resolution (CANCELLED).
  CLAIMED: ['FULFILLED', 'CANCELLED'],
  FULFILLED: [],
  REJECTED: [],
  CANCELLED: [],
  REVOKED: [],
  EXPIRED: [],
};

export function canTransition(from: IndentStatus, to: IndentStatus): boolean {
  return INDENT_TRANSITIONS[from].includes(to);
}

/**
 * Driver acknowledgement is tracked separately so it can never erase a
 * supply. Disputes are tracked separately again (disputeOpen), so raising a
 * dispute does not change the acknowledgement or the debt.
 */
/** NOT_REQUIRED: a scanned indent's fill needs no confirmation unless the fleet asks for it. */
export const ACK_STATUSES = ['NOT_DUE', 'PENDING', 'ACKNOWLEDGED', 'ACKNOWLEDGED_BY_ACCOUNTS', 'NOT_REQUIRED'] as const;
export type AckStatus = (typeof ACK_STATUSES)[number];

export const INDENT_STATUS_LABELS: Record<IndentStatus, string> = {
  DRAFT: 'Draft',
  REQUESTED: 'Waiting for approval',
  APPROVED: 'Approved – ready to use',
  REJECTED: 'Rejected',
  CLAIMED: 'Fuel being filled',
  FULFILLED: 'Supplied',
  CANCELLED: 'Cancelled',
  REVOKED: 'Stopped by company',
  EXPIRED: 'Expired',
};

export const ACK_STATUS_LABELS: Record<AckStatus, string> = {
  NOT_DUE: '—',
  PENDING: 'Driver confirmation pending',
  ACKNOWLEDGED: 'Confirmed by driver',
  ACKNOWLEDGED_BY_ACCOUNTS: 'Confirmed by company accounts',
  NOT_REQUIRED: 'Recorded by the pump',
};

export const CATEGORIES = ['FUEL', 'TYRES', 'SPARES', 'SERVICE', 'OTHER'] as const;
export type Category = (typeof CATEGORIES)[number];

export const FUEL_TYPES = ['PETROL', 'DIESEL', 'CNG', 'PREMIUM_PETROL', 'PREMIUM_DIESEL'] as const;
export type FuelType = (typeof FUEL_TYPES)[number];

export const FUEL_TYPE_LABELS: Record<FuelType, string> = {
  PETROL: 'Petrol',
  DIESEL: 'Diesel',
  CNG: 'CNG',
  PREMIUM_PETROL: 'Premium petrol',
  PREMIUM_DIESEL: 'Premium diesel',
};

export const RELATIONSHIP_STATUSES = [
  'INVITED',
  'TERMS_PROPOSED',
  'PENDING_OPENING_BALANCE',
  'ACTIVE',
  'CLOSED',
] as const;
export type RelationshipStatus = (typeof RELATIONSHIP_STATUSES)[number];

export const BILLING_CYCLES = ['WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'CUSTOM_DATES'] as const;
export type BillingCycle = (typeof BILLING_CYCLES)[number];

export const AGEING_BUCKETS = [
  'NOT_BILLED',
  'CURRENT',
  'DUE_SOON',
  'DUE_TODAY',
  'OVERDUE_1_7',
  'OVERDUE_8_30',
  'OVERDUE_30_PLUS',
] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

export const AGEING_LABELS: Record<AgeingBucket | 'SUSPENDED', string> = {
  NOT_BILLED: 'Not billed yet',
  CURRENT: 'Billed, not due yet',
  DUE_SOON: 'Due soon',
  DUE_TODAY: 'Due today',
  OVERDUE_1_7: '1–7 days overdue',
  OVERDUE_8_30: '8–30 days overdue',
  OVERDUE_30_PLUS: 'More than 30 days overdue',
  SUSPENDED: 'Account stopped',
};

export const LEDGER_KINDS = [
  'SUPPLY',
  'MANUAL_SUPPLY',
  'OPENING_BALANCE',
  'LEGACY_IMPORT',
  'DEBIT_ADJUSTMENT',
  'CREDIT_ADJUSTMENT',
  'PAYMENT_ALLOCATION',
  'ALLOCATION_REVERSAL',
] as const;
export type LedgerKind = (typeof LEDGER_KINDS)[number];

/** Entries that create a receivable item (something the buyer owes, with a due date). */
export const ITEM_KINDS: LedgerKind[] = ['SUPPLY', 'MANUAL_SUPPLY', 'OPENING_BALANCE', 'LEGACY_IMPORT', 'DEBIT_ADJUSTMENT'];

export const LEDGER_KIND_LABELS: Record<LedgerKind, string> = {
  SUPPLY: 'Supply',
  MANUAL_SUPPLY: 'Supply (recorded by hand)',
  OPENING_BALANCE: 'Opening balance',
  LEGACY_IMPORT: 'Earlier bill (imported)',
  DEBIT_ADJUSTMENT: 'Extra charge (approved)',
  CREDIT_ADJUSTMENT: 'Reduction / credit note',
  PAYMENT_ALLOCATION: 'Payment applied',
  ALLOCATION_REVERSAL: 'Payment undone',
};

/** Oil marketing company a pump is dealer-owned or franchised under. */
export const OMCS = ['IOCL', 'BPCL', 'HPCL', 'NAYARA', 'SHELL', 'JIO_BP', 'MRPL', 'OTHER'] as const;
export type Omc = (typeof OMCS)[number];

export const OMC_LABELS: Record<Omc, string> = {
  IOCL: 'Indian Oil (IOCL)',
  BPCL: 'Bharat Petroleum (BPCL)',
  HPCL: 'Hindustan Petroleum (HPCL)',
  NAYARA: 'Nayara Energy',
  SHELL: 'Shell',
  JIO_BP: 'Jio-bp',
  MRPL: 'MRPL',
  OTHER: 'Other / independent',
};
